import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ClassConstructor, plainToInstance } from "class-transformer";
import { FieldValues } from "react-hook-form";
import { ApiRes, CommonRes, ErrorRes, PlainRes, SuccessRes } from "@/dto/HttpDto.ts";
import { EndpointConfig, EndpointEnum } from "@/enum/EndpointEnum.ts";
import format from "string-format";
import ConvertUtil from "@/util/ConvertUtil.ts";
import HttpException from "@/exception/HttpException.ts";
import { NetworkErrorCodeEnum } from "@/enum/NetworkErrorCodeEnum.ts";
import { getToken } from "@/config/Keycloak.ts";
import { saveAs } from "file-saver";
import { parse } from "content-disposition-header";

// import FormData from "form-data";

export interface HttpConfigInterface<TRequest extends PlainRes = PlainRes, TResponse extends SuccessRes = SuccessRes> {
    endpointEnum: EndpointEnum;
    params?: Partial<TRequest>;
    headers?: Record<string, any>;
    responseDto?: ClassConstructor<TResponse>;
}

/**
 * 기본 axios instance 생성
 */
export const AXIOS_INSTANCE: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_BACKEND_HOST,
});

// Request 인터셉터 ( 토큰 설정용 )
AXIOS_INSTANCE.interceptors.request.use(
    async config => {
        const token = await getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    },
);

AXIOS_INSTANCE.interceptors.response.use(
    response => response,
    error => {
        if (error instanceof Error) {
            return Promise.reject(error);
        }

        return Promise.reject(new Error("알 수 없는 에러입니다."));
    },
);

class HttpUtil {
    public static async axiosRequest<TRequest extends FieldValues = FieldValues, TResponse extends SuccessRes = SuccessRes>({
        endpointEnum,
        params,
        headers,
        responseDto,
    }: HttpConfigInterface<TRequest, TResponse>): Promise<ApiRes<TResponse>> {
        const config = EndpointConfig[endpointEnum];
        let url = config.uri;

        if (params) {
            /**
             * {uuid} 의 형태가 있는 경우, replace 해줌
             */
            url = format(url, params);
        }

        const axiosConfig: AxiosRequestConfig = {
            method: config.method,
            withCredentials: true,
            url,
            responseType: (config.responseType as AxiosRequestConfig["responseType"]) || "json",
            headers: {
                "Content-Type": config.contentType || "application/json",
                ...headers,
            },
            validateStatus: status => {
                return status < 500;
            },
        };

        if (config.method === "GET") {
            axiosConfig.params = params;
            axiosConfig.paramsSerializer = {
                serialize: () => ConvertUtil.queryStringify(params, false),
            };
        } else if (config.contentType === "multipart/form-data" && params) {
            const formData = new FormData();
            Object.keys(params).forEach(key => {
                if (!params[key]) {
                    return;
                }
                if (key === "req") {
                    formData.append(key, new Blob([JSON.stringify(params[key])], { type: "application/json" }), "");
                } else {
                    formData.append(key, params[key]);
                }
            });
            axiosConfig.data = formData;
        } else {
            axiosConfig.data = params;
        }

        console.info(`%c REQUEST`, "font-weight:bold;color:orangered;font-size:14px", axiosConfig);
        let fetchResponse: AxiosResponse<ApiRes<CommonRes<SuccessRes>>> | undefined;
        try {
            fetchResponse = await AXIOS_INSTANCE(axiosConfig);
        } catch (e) {
            console.error(e);
            const error: ErrorRes = {
                error: NetworkErrorCodeEnum.UNKNOWN_ERROR,
                message: "연결상태가 좋지 않습니다. 인터넷 연결 상태를 확인 후 다시 시도해주세요.",
            };

            if (e instanceof AxiosError) {
                const json = e.toJSON();
                if ("message" in json && typeof json.message === "string") {
                    error.message = json.message;
                }
            }
            throw new HttpException(error);
        }

        if (!fetchResponse) {
            throw new HttpException({
                error: NetworkErrorCodeEnum.UNKNOWN_ERROR,
                message: "알 수 없는 에러입니다.\n관리자에게 문의해주세요.",
            });
        }

        if (!fetchResponse.headers.hasContentLength || !fetchResponse.headers.getContentLength) {
            throw new HttpException({
                error: NetworkErrorCodeEnum.EMPTY_RESPONSE,
                message: "빈 응답입니다.\n관리자에게 문의해주세요.",
            });
        }

        let response: ApiRes<CommonRes<SuccessRes>> | BlobPart = fetchResponse.data;

        /**
         * 변환 보고서 파일 다운로드
         * */
        const contentDispositionHeader = fetchResponse.headers["content-disposition"];

        if (contentDispositionHeader) {
            const parsed = parse(contentDispositionHeader);
            const filename = parsed.parameters.filename || "inspection-report.pdf";
            saveAs(response as unknown as Blob, filename);

            return {} as ApiRes<TResponse>;
        }

        if (fetchResponse.status >= 400) {
            const fetchData = fetchResponse.data;
            response = {
                message: fetchData && "data" in fetchData && fetchData.data ? String(fetchData.data) : fetchData.message || "",
                error: NetworkErrorCodeEnum.UNKNOWN_ERROR,
            };
        }
        console.info(`%c RESPONSE`, "font-weight:bold;color:skyblue;font-size:14px", response);
        if (response && "error" in response) {
            return response;
        }

        if (responseDto) {
            return plainToInstance<TResponse, SuccessRes>(responseDto, response.data);
        }
        return response.data as TResponse;
    }
}

export default HttpUtil;
